import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import Draggable from "@/components/draggable";
import styles from "@/components/shortcut.module.css";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { BookmarkInfo, DialogIcon, PositionInfo } from "@/types";
import {
	getRedirectLink,
	handleKeyboardClick,
	handleLongPress,
	logIn,
	openEditShortcut,
	openWebPage,
	showDialog,
	showMenu,
} from "@/utils";
import {
	faCircleMinus,
	faCirclePlus,
	faPenToSquare,
	faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { CSSProperties, JSX, useRef, useState } from "react";
import { LongPressEventType, useLongPress } from "use-long-press";

interface ShortcutProps {
	dragId: string;
	hasPopup?: boolean;
	isDraggable: boolean;
	isInDock: boolean;
	isInFolder?: boolean;
	item: BookmarkInfo;
	setIsShortcutTitleShown?: (isShown: boolean) => void;
	setShortcutTitlePosition?: (position: PositionInfo) => void;
	setShortcutTitleText?: (text: string) => void;
}

function Shortcut({
	dragId,
	hasPopup,
	isDraggable,
	isInDock,
	isInFolder,
	item,
	setIsShortcutTitleShown,
	setShortcutTitlePosition,
	setShortcutTitleText,
}: ShortcutProps): JSX.Element {
	const { color, icon, id, itemType, onClick, title, url } = item;

	const queryClient = useQueryClient();

	const [isImageLoadingFailed, setIsImageLoadingFailed] =
		useState<boolean>(false);

	const shortcutRef = useRef<HTMLDivElement>(null);

	const handleClick = (event?: React.MouseEvent<HTMLDivElement>): void => {
		event?.stopPropagation();
		if (signals.isDragging.value) {
			return;
		}
		if (url) {
			if (url.startsWith(globals.REDIRECT_PREFIX)) {
				const redirectId = url.replace(globals.REDIRECT_PREFIX, "");
				openWebPage(getRedirectLink(redirectId));
			} else {
				openWebPage(url);
			}
		} else if (onClick) {
			void onClick(event);
		}
	};

	const handleContextMenu = (
		event: MouseEvent | React.MouseEvent<HTMLDivElement>,
	): void => {
		if (signals.isDragging.value || !isDraggable) {
			return;
		}
		showMenu(
			event,
			[
				{
					content: {
						icon: faPenToSquare,
						text: t("edit"),
					},
					onClick: (event?: React.MouseEvent<Element>): void => {
						openEditShortcut({
							bookmark: item,
							event,
						});
					},
					when: !isInDock && item.itemType !== "preset",
				},
				{
					content: {
						icon: faCirclePlus,
						text: t("addToDock"),
					},
					onClick: async (): Promise<void> => {
						if (!id || !signals.login.value.username) {
							await handleNotLoggedIn();
							return;
						}
						signals.isLoadingShown.value = true;
						try {
							await new QueryDataManager(queryClient).addToDock({
								id,
								type: item.itemType,
							});
						} catch (error) {
							void api.handleApiError(error);
						} finally {
							signals.isLoadingShown.value = false;
						}
					},
					when: !isInDock,
				},
				{
					content: {
						icon: faTrashCan,
						isDangerous: true,
						text: t("delete"),
					},
					onClick: async (): Promise<void> => {
						if (!id) {
							throw new Error("ID not found");
						}
						await showDialog(t("confirmDeleteThisShortcut"), {
							icon: DialogIcon.WARNING,
							showCancel: true,
							title: t("confirm"),
						});
						if (signals.login.value.username) {
							signals.isLoadingShown.value = true;
							try {
								await new QueryDataManager(
									queryClient,
								).deleteBookmark(
									id,
									signals.currentFolder.value?.id,
								);
							} catch (error) {
								void api.handleApiError(error);
							} finally {
								signals.isLoadingShown.value = false;
							}
						} else if (item.itemType === "preset") {
							new QueryDataManager(
								queryClient,
							).removePresetShortcut(id);
						}
					},
					when: !isInDock,
				},
				{
					content: {
						icon: faCircleMinus,
						text: t("removeFromDock"),
					},
					onClick: async (): Promise<void> => {
						if (!id || !signals.login.value.username) {
							await handleNotLoggedIn();
							return;
						}
						signals.isLoadingShown.value = true;
						try {
							await new QueryDataManager(
								queryClient,
							).removeFromDock(id);
						} catch (error) {
							console.error(error);
						} finally {
							signals.isLoadingShown.value = false;
						}
					},
					when: isInDock,
				},
			],
			{
				shouldBeLighterInDarkTheme: true,
			},
		);
	};

	const handleNotLoggedIn = async (): Promise<void> => {
		if (signals.login.value.username) {
			throw new Error("ID not found");
		} else {
			await showDialog(t("shortcutsSyncWithAccountLoginFirst"), {
				showCancel: true,
			});
			logIn();
		}
	};

	const handleImageError = (): void => {
		setIsImageLoadingFailed(true);
	};

	const handleMouseEnter = (): void => {
		if (
			setIsShortcutTitleShown &&
			setShortcutTitlePosition &&
			setShortcutTitleText
		) {
			const rect = shortcutRef.current?.getBoundingClientRect();
			setShortcutTitlePosition({
				left: (rect?.left ?? 0) + 20,
				top: (rect?.top ?? 0) - 50,
			});
			setShortcutTitleText(title || "");
			setIsShortcutTitleShown(true);
		}
	};

	const handleMouseLeave = (): void => {
		if (setIsShortcutTitleShown) {
			setIsShortcutTitleShown(false);
		}
	};

	const longPress = useLongPress(handleLongPress(handleContextMenu), {
		detect: LongPressEventType.Touch,
		threshold: 500,
	});

	return (
		<Draggable
			dragId={dragId}
			isDraggable={isDraggable}
		>
			<div
				aria-haspopup={hasPopup ? "dialog" : undefined}
				aria-hidden={isInFolder}
				aria-label={isInDock ? title : undefined}
				className={clsx(
					styles["shortcut"],
					itemType === "action" && styles["action"],
					signals.generalSettings.value
						.isBackdropBlurEnabledForShortcuts &&
						styles["backdrop-blur"],
					signals.isDragging.value && styles["dragging"],
					isInDock && styles["in-dock"],
					isInFolder && styles["in-folder"],
				)}
				onClick={handleClick}
				onContextMenu={handleContextMenu}
				onKeyDown={handleKeyboardClick(handleClick)}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				ref={shortcutRef}
				role={isInFolder ? "presentation" : "button"}
				tabIndex={isInFolder ? -1 : 0}
				title={isInDock ? undefined : title}
				{...longPress()}
			>
				{icon?.startsWith("http") && !isImageLoadingFailed ? (
					<img
						alt=""
						draggable={false}
						height={24}
						loading="lazy"
						onError={handleImageError}
						referrerPolicy="no-referrer"
						src={icon}
						width={24}
					/>
				) : icon?.startsWith("color://") || isImageLoadingFailed ? (
					<div
						className={styles["generated-icon"]}
						style={((): CSSProperties => {
							const colorSet = isImageLoadingFailed
								? ["#9ba7b1", "#fff"]
								: icon.replace("color://", "").split("&");
							return {
								backgroundColor: colorSet[0],
								color: colorSet[1],
							};
						})()}
					>
						{title.substring(0, 1)}
					</div>
				) : (
					<i style={{ backgroundImage: color }}>{icon}</i>
				)}
				{!isInDock && !isInFolder && itemType !== "action" && (
					<div className={styles["shortcut-title"]}>{title}</div>
				)}
			</div>
		</Draggable>
	);
}

export default Shortcut;
